import React, {
  useContext, useEffect, useState, useRef, useMemo, useCallback
} from 'react';
import proptypes from 'prop-types';
const { func, string, bool } = proptypes;
import {
  arrayOf as arrayType,
  extend,
  params,
  number as numberType,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  customType,
  QueryContext,
  useDataModel,
  QueryProvider,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { LinkTag, useHelmet } from '@thd-nucleus/thd-helmet';
import { ListView, ListViewPlaceHolder } from './list-view';
import { ListHeader, ListHeaderSkeleton } from './list-header';
import { PrivateListMessaging } from './private-list-messaging/PrivateListMessaging';
import { useListProduct } from './useListProduct';
import { ProductListContext } from './ProductListContext';
import {
  addMissingField,
  addToProductsDisplayed,
  arrayUnique,
  calculateLineItemType,
  getItemIdsMissingInPages,
  getQuantity,
  isPureIntentItem,
  NO_LIST_EXIST_ERROR,
  PRIVATE_LIST_ERROR,
  REMOVE_PRODUCT,
  returnArray,
  returnObject,
  triggerPostPageErrorAnalytics
} from './productListHelper';
import { ListNotFoundMessaging } from './list-not-found-messaging/ListNotFoundMessaging';
import { SaveSharedList } from './save-shared-list/SaveSharedList';
import { ProductListLoader } from './ProductListLoader';
import { ActionBarPlaceHolder } from './action-bar/ActionBarPlaceHolder';
import { useProducts } from './hooks/useProducts';

const ProductList = (props) => {
  const {
    id,
    onDelete,
    isSharedList,
    onSaveSharedList,
    onAnalyticsData
  } = props;

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-list.ready'); }, []);
  const { channel } = useContext(ExperienceContext);
  const { storeId, isLocalized, storeZip } = useStore({ varnish: true });
  const isMobile = channel === 'mobile';
  const zipCode = podFulFillmentUtils.getDeliveryZipCookie() || storeZip;
  const { isClientResolved, defaultVariables } = useContext(QueryContext);
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track which groups are expanded
  const [productsDisplayed, setProductsDisplayed] = useState([]);
  const [isAddedMaterials, setIsAddedMaterials] = useState(false);
  const [isIntersecting, setIntersecting] = useState(false);
  // Product data management for ATC functionality
  const [productData, setProductData] = useState([]);
  const [loadedItemIds, setLoadedItemIds] = useState([]);
  const [resolvedIntentItemId, setResolvedIntentItemId] = useState([]);

  // Replace feature state management
  const [replacingItemId, setReplacingItemId] = useState(null);
  const [replacingIntentId, setReplacingIntentId] = useState(null);
  const [replacingGroupId, setReplacingGroupId] = useState(null);
  const [replacingIntent, setReplacingIntent] = useState(null);
  const [replacingQuantity, setReplacingQuantity] = useState(1);
  const isReplaceMode = replacingItemId !== null;

  // Replace feature functions - defined early to be available for use in other functions
  const setReplaceMode = useCallback(({
    itemId, intentId, groupId, intent, quantity
  }) => {
    setReplacingItemId(itemId);
    setReplacingIntentId(intentId || null);
    setReplacingGroupId(groupId || null);
    setReplacingIntent(intent || '');
    setReplacingQuantity(quantity || 1);
  }, []);

  const cancelReplaceMode = useCallback(() => {
    setReplacingItemId(null);
    setReplacingIntentId(null);
    setReplacingGroupId(null);
    setReplacingIntent(null);
    setReplacingQuantity(1);
  }, []);

  const {
    list,
    listProducts,
    showLoader,
    productInfoProducts,
    productInfoLoading,
    refetch,
    productRefetch,
    error,
    clearListFromCache,
    loadItemsOnDemand
  } = useListProduct({
    page,
    id,
    isSharedList,
    isAddedMaterials,
    expandedGroups
  });
  // Initialize useProducts hook for full product data loading
  const { fetchProducts } = useProducts({
    setProductData,
    setLoadedItemIds,
    loadedItemIds
  });

  const isEmptyList = useMemo(() => {
    const listItemsIds = (list?.groups || []).map((item) => {
      return (item?.items || []).map((itm) => itm.itemId);
    }).flat() || [];
    return Boolean(listItemsIds.length < 1);
  }, [list]);

  const onListDelete = () => {
    clearListFromCache(id);
    onDelete();
  };

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupTotals, setGroupTotals] = useState({});
  const [analyticsTriggered, setAnalyticsTriggered] = useState(false);

  // Reset group totals when list becomes completely empty
  useEffect(() => {
    const hasAnyItems = list?.groups?.some((group) => group.items && group.items.length > 0);
    const hasAnyProducts = productInfoProducts && productInfoProducts.length > 0;

    // If list is empty but we still have group totals, reset them using functional update
    if (!hasAnyItems && !hasAnyProducts) {
      setGroupTotals((prevTotals) => {
        // Only update if there are actually totals to clear
        return Object.keys(prevTotals).length > 0 ? {} : prevTotals;
      });
    }
  }, [list?.groups, productInfoProducts]); // Kept without groupTotals to prevent infinite loop

  // Create stable analytics dependencies to prevent unnecessary re-renders
  const analyticsData = useMemo(() => {
    // Wait for list to be loaded and have an ID, and don't calculate during loading
    // Allow empty lists (listProducts can be empty array but not undefined)
    if (showLoader || !list?.id) {
      return null;
    }

    // Calculate group analytics data
    const groups = list.groups || [];

    // Calculate groupTotal (number of groups)
    const groupTotal = groups.length;

    // Calculate groupDetail (products per group) - format: "1>3|2>5|3>2"
    const groupDetailParts = groups.map((group, index) => {
      const groupIndex = index + 1; // 1-based indexing
      const itemCount = group.items ? group.items.length : 0;
      return `${groupIndex}>${itemCount}`;
    });
    const groupDetail = groupDetailParts.join('|');

    // Calculate groupNames - format: "default|flooring|cabinets"
    // Handle PII filtering and default group naming per requirements
    const groupNames = groups.map((group) => {
      let groupName = group.groupName;

      // Handle default group (null groupId or groupName)
      if (group.groupId === null || group.groupId === '__ungrouped__' || !groupName) {
        return 'default';
      }

      // Basic PII filtering - look for common patterns
      const piiPatterns = [
        /@/, // Email addresses
        /\d{3}-\d{3}-\d{4}/, // Phone numbers
        /\d{4,5}\s+\w+/, // Street addresses (numbers + street)
      ];

      const hasPii = piiPatterns.some((pattern) => pattern.test(groupName));
      return hasPii ? 'pii' : groupName.toLowerCase();
    }).join('|');

    // Calculate other list metrics
    // Use productInfoProducts for calculations since it has the enriched product data
    const uniqueSkuCount = (productInfoProducts || []).length;

    // Get quantities from groups since listProducts might be empty/paginated
    // Each group has items with quantity information
    let quantityTotal = 0;

    // Calculate listTotal from the sum of all group subtotals (from GroupSubTitle API calls)
    const listTotal = Object.values(groupTotals).reduce((sum, total) => sum + (total || 0), 0);
    if (list?.groups && productInfoProducts?.length > 0) {
      // Build a quantity map from groups for quantityTotal calculation
      const quantityMap = {};
      list.groups.forEach((group) => {
        if (group.items) {
          group.items.forEach((item) => {
            quantityMap[item.itemId] = (quantityMap[item.itemId] || 0) + (item.quantity || 0);
          });
        }
      });

      // Calculate quantityTotal from group items
      quantityTotal = Object.values(quantityMap).reduce((sum, qty) => sum + qty, 0);
    }

    // Fallback: if we still don't have quantities, try from listProducts or productInfoProducts directly
    if (quantityTotal === 0 && listProducts?.length > 0) {
      quantityTotal = listProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    }
    if (quantityTotal === 0 && productInfoProducts?.length > 0) {
      quantityTotal = productInfoProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    }

    return {
      subTotal: list.subTotal || 0,
      productCount: (listProducts || []).length,
      quantityTotal,
      // Group-specific analytics data
      groupTotal,
      groupDetail,
      groupNames,
      uniqueSkuCount,
      listTotal
    };
  }, [list?.subTotal, list?.groups, listProducts, productInfoProducts, groupTotals]);

  // Analytics data calculation - separate useEffect since it depends on list data, not productInfoProducts
  useEffect(() => {
    // Only trigger analytics when we have actual product data loaded
    const hasProductData = productInfoProducts && productInfoProducts.length > 0;
    const hasListData = list && list.groups && list.groups.length > 0;
    // Check that we have ALL group totals from groups that have items
    // Empty groups are excluded from groupTotals by ListView.js logic
    const groupsWithItems = list?.groups?.filter((group) => group.items && group.items.length > 0) || [];
    const expectedGroupCount = groupsWithItems.length;

    // Wait for all groups with items to have actual totals > 0, allow 0 for empty groups or groups with only intents
    const groupsWithItemsHaveActualTotals = groupsWithItems.every((group) => {
      const groupKey = group.groupId || '__ungrouped__';
      const groupTotal = groupTotals[groupKey];

      // Check if group contains only intent items (no catalog products)
      const groupHasOnlyIntents = group.items && group.items.every((item) => isPureIntentItem(item));

      // Groups with only intents are considered ready immediately since they don't contribute to pricing
      if (groupHasOnlyIntents) {
        return true;
      }
      return groupTotal !== undefined && groupTotal > 0; // Regular groups with products must have totals > 0

    });

    // For empty groups, just check they're in groupTotals (can be 0)
    const emptyGroups = list?.groups?.filter((group) => !group.items || group.items.length === 0) || [];
    const emptyGroupsReported = emptyGroups.every((group) => {
      const groupKey = group.groupId || '__ungrouped__';
      if (groupTotals[groupKey] === undefined) {
        setGroupTotals((prevTotals) => ({
          ...prevTotals,
          [groupKey]: 0
        }));
      }
      return groupTotals[groupKey] !== undefined; // Empty groups can be 0, just need to be reported
    });

    const hasAllGroupTotals = expectedGroupCount > 0 && groupsWithItemsHaveActualTotals && emptyGroupsReported;

    if (isEmptyList && !analyticsTriggered && typeof onAnalyticsData === 'function' && list?.id) {
      setAnalyticsTriggered(true);
      onAnalyticsData({ myList: {} });
    }

    if (typeof onAnalyticsData === 'function' && analyticsData && !analyticsTriggered
        && hasProductData && hasListData && hasAllGroupTotals) {
      const analyticsPayload = {
        myList: {
          listTotal: analyticsData.listTotal.toFixed(2), // Format as currency string
          uniqueSkuCount: analyticsData.uniqueSkuCount,
          quantityTotal: analyticsData.quantityTotal,
          groupTotal: analyticsData.groupTotal,
          groupDetail: analyticsData.groupDetail,
          groupNames: analyticsData.groupNames,
          lineItemType: calculateLineItemType(list)
        }
      };
      setAnalyticsTriggered(true);
      onAnalyticsData(analyticsPayload);
    }
  }, [analyticsData, onAnalyticsData, analyticsTriggered, productInfoProducts, list]);

  const theme = useTheme(ProductList, props);
  const { showSaveSharedListBanner } = theme.props;

  // Generate images that need to be pre-fetched, added some conditions to check if the product
  // images are available because some items are not returned by products
  const preFetchImages = useRef([]);
  if (productInfoProducts?.length && !productsDisplayed.length) {
    const productImageToPrefetch = Math.min(isMobile ? 1 : 2, productInfoProducts.length);
    preFetchImages.current = productInfoProducts.slice(0, productImageToPrefetch)
      .map((product) => product?.media?.images?.find((img) => img.subType === 'PRIMARY')?.url)
      .filter(Boolean);
  }

  const generatePreFetchTags = ({ data: preFetchImageUrls }) => {
    return (returnArray(preFetchImageUrls)).map((imgUrl, index) => (new LinkTag({
      rel: 'preload',
      href: imgUrl.replace(/<SIZE>/g, '300'),
      id: `preloadImg-${index}`,
      as: 'image',
      media: '(max-width: 300px)',
      fetchpriority: 'high'
    })));
  };
  useHelmet('list-details', { data: preFetchImages.current }, generatePreFetchTags, [id]);

  // Mutation for list operations (delete groups, remove items)
  const [listOperationsMutation] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    context: { withAuth: true },
    lazy: true
  });

  // Mutation to remove item(s) from a list
  // const [removeListItemsMutation, { loading: isRemoving }] = useDataModel('updateList', {
  //   variables: {
  //     id: list?.id
  //   },
  //   context: { withAuth: true },
  //   lazy: true
  // });

  // Mutation for replace item functionality
  const [replaceItemMutation] = useLazyDataModel('replaceItem', {
    variables: {
      id: list?.id
    },
    context: { withAuth: true }
  });

  const [productInfoRequest, productInfoResponse] = useLazyDataModel('products', {
    variables: {
      storeId,
      zipCode
    }
  });

  const { data: productInfoData, variables: productInfoVariables } = productInfoResponse;

  useEffect(() => {
    // when a new page product data is loaded
    if (productInfoProducts?.length) {
      // If a refetch happens and it's still loading,
      // do not update the products to display context state as it has the first fetch data
      if (productInfoLoading) return;
      const productsToDisplay = addToProductsDisplayed({
        productInfoProducts, productsDisplayed, isAddedMaterials
      });
      // if added value is true, set to false
      if (isAddedMaterials) { setIsAddedMaterials(false); }

      setProductsDisplayed(productsToDisplay);

      // Trigger analytics when products are loaded and all group totals are available
      const hasAllGroupTotals = list?.groups?.length > 0
        ? Object.keys(groupTotals).length >= list.groups.length
        : true; // No groups means totals are ready

      if (productInfoProducts.length > 0 && hasAllGroupTotals) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('my-list.products-load', { products: productsDisplayed });
      }

      if (page > 1) {
        LIFE_CYCLE_EVENT_BUS.trigger('product-list.pagination', { page });
      }
    }
  }, [productInfoProducts]);

  useEffect(() => {
    // when missed products data is loaded, this happens in the scenario: if 3 pages out of 4 total
    // pages is loaded, if items in 1st page and 1 another item in second page is deleted, one item
    // in the new second page will be missing, that will be loaded here.
    const products = productInfoData?.products;
    if (products?.length) {
      const missingProducts = addMissingField({
        itemIds: productInfoVariables?.itemIds,
        productData: products,
        listProducts
      });
      setProductsDisplayed([
        ...productsDisplayed,
        ...missingProducts
      ]);
    }
  }, [productInfoData]);

  useEffect(() => {
    const gqlError = error?.graphQLErrors?.[0];
    if (gqlError) {
      const isPrivateListError = gqlError?.message.includes(PRIVATE_LIST_ERROR);
      const type = isPrivateListError ? 'advisory' : 'error';
      triggerPostPageErrorAnalytics({
        type,
        message: gqlError?.message,
        code: isPrivateListError ? '401' : '404',
        isSharedList
      });
    }
  }, [error]);

  useEffect(() => {
    const gqlError = error?.graphQLErrors?.[0];
    if (!showLoader && (!productsDisplayed.length && !productInfoProducts?.length) && !gqlError) {
      triggerPostPageErrorAnalytics({
        type: 'advisory',
        message: 'A Blank List. A Fresh Start',
        code: 'n/a',
        isSharedList
      });
    }
  }, [showLoader]);

  const skipFn = useCallback(({ skip, queryName, attributes }) => {
    if (queryName !== 'product' || isEmptyList) return skip;
    const isResolved = isClientResolved({ queryName: 'products' });
    if (attributes.product?.fulfillment) {
      if (isResolved) {
        return skip;
      }
      return true;
    }
    return skip;
  }, [isClientResolved, isEmptyList]);

  const onProductSelectionChange = ({ itemIds, isAdd }) => {
    if (itemIds) {
      if (isAdd) {
        setSelectedItems(arrayUnique([
          ...selectedItems,
          ...itemIds
        ]));
      } else {
        const newSelected = (returnArray(selectedItems)).map((itemId) => {
          if (itemIds.includes(itemId)) {
            return null;
          }
          return itemId;
        }).filter((itemId) => itemId !== null);
        setSelectedItems([...newSelected]);
      }
    }
  };

  const onGroupSelectionChange = ({ groupIds, isAdd }) => {
    if (groupIds) {
      if (isAdd) {
        setSelectedGroups(arrayUnique([
          ...selectedGroups,
          ...groupIds
        ]));
      } else {
        const newSelected = (returnArray(selectedGroups)).map((groupId) => {
          if (groupIds.includes(groupId)) {
            return null;
          }
          return groupId;
        }).filter((groupId) => groupId !== null);
        setSelectedGroups([...newSelected]);
      }
    }
  };

  const removeListItems = async (itemIds = null) => {
    // Cancel replace mode when user performs another action
    cancelReplaceMode();

    // If no itemIds provided, use the combined selection of items and groups
    const idsToRemove = itemIds || [...selectedItems, ...selectedGroups];

    // Separate groups and individual items
    const groupIds = [];
    const individualItemIds = [];

    idsToRemove.forEach((itemId) => {
      // Check if this id is a group by looking in list.groups
      // Handle special case for "__ungrouped__" which represents items with groupId: null
      const actualGroupId = itemId === '__ungrouped__' ? null : itemId;
      const isGroup = list?.groups?.some((group) => group.groupId === actualGroupId);
      if (isGroup) {
        groupIds.push(itemId); // Keep frontend ID for consistency
      } else {
        individualItemIds.push(itemId);
      }
    });

    // Handle group deletion using Delete operation
    if (groupIds.length > 0) {
      // Build groups array with groupName, groupId, and groupOrder
      const groupsToDelete = groupIds.map((groupId) => {
        // Convert frontend ID back to backend ID for lookup
        const actualGroupId = groupId === '__ungrouped__' ? null : groupId;
        const group = list?.groups?.find((grp) => grp.groupId === actualGroupId);
        return {
          groupName: group?.groupName || '',
          groupId: actualGroupId, // Use backend ID for the mutation
          groupOrder: group?.groupOrder || 1
        };
      }).filter((group) => group.groupName !== undefined); // Allow empty group names, just filter undefined

      await listOperationsMutation({
        variables: {
          operationName: 'Delete',
          id: list.id,
          groups: groupsToDelete
        }
      });

      // Refetch the list data after successful group deletion
      await refetch();
    }

    // Handle individual item deletion
    if (individualItemIds.length > 0) {
      // Separate items into grouped and ungrouped, but exclude items from groups being deleted
      const groupedItems = [];
      const ungroupedItems = [];

      individualItemIds.forEach((itemId) => {
        // Find which group this item belongs to
        let groupId = null;
        let isIntentItem = false;
        if (list?.groups) {
          list.groups.forEach((group) => {
            // Check for regular items by itemId
            if (group.items?.some((item) => item.itemId === itemId)) {
              groupId = group.groupId;
            }
            // Check for intent items by intentId
            if (group.items?.some((item) => item.intentId === itemId)) {
              groupId = group.groupId;
              isIntentItem = true;
            }
          });
        }

        // Only process items if their group is NOT being deleted
        if (groupId && !groupIds.includes(groupId)) {
          groupedItems.push({ itemId, groupId, isIntentItem });
        } else if (!groupId) {
          ungroupedItems.push(itemId);
        }
        // If groupId is in groupIds, skip this item as it will be deleted with the group
      });

      // Handle ungrouped items with listOperations mutation
      if (ungroupedItems.length > 0) {
        // Separate regular items and intent items for ungrouped
        const regularItems = [];
        const intentItems = [];

        ungroupedItems.forEach((itemId) => {
          // Check if this is an intent item
          const isIntent = list?.groups?.some((group) => group.items?.some((item) => item.intentId === itemId));

          if (isIntent) {
            intentItems.push({ intentId: itemId });
          } else {
            regularItems.push({ itemId });
          }
        });

        const products = [...regularItems, ...intentItems];

        await listOperationsMutation({
          variables: {
            operationName: 'RemoveItems',
            id: list.id,
            groups: [{
              products
            }]
          }
        });
        // Clear cache and refetch after removing ungrouped items
        clearListFromCache(list.id);
        await refetch();
      }

      // Handle grouped items with listOperations mutation
      if (groupedItems.length > 0) {
        // Group items by their groupId for the listOperations call
        const itemsByGroup = {};

        groupedItems.forEach(({ itemId, groupId, isIntentItem }) => {
          if (!itemsByGroup[groupId]) {
            itemsByGroup[groupId] = [];
          }

          if (isIntentItem) {
            itemsByGroup[groupId].push({ intentId: itemId });
          } else {
            itemsByGroup[groupId].push({ itemId });
          }
        });

        // Build groups array for listOperations
        const groupsForRemoval = Object.entries(itemsByGroup).map(([groupId, products]) => ({
          groupId,
          products
        }));

        await listOperationsMutation({
          variables: {
            operationName: 'RemoveItems',
            id: list.id,
            groups: groupsForRemoval
          }
        });
        // Clear cache and refetch after removing grouped items
        clearListFromCache(list?.id);
        await refetch();
      }

      // Analytics and state updates for individual items (excluding items from deleted groups)
      const itemsForAnalytics = individualItemIds.filter((itemId) => {
        // Find which group this item belongs to
        let itemGroupId = null;
        if (list?.groups) {
          list.groups.forEach((group) => {
            // Check both regular items and intent items
            if (group.items?.some((item) => item.itemId === itemId || item.intentId === itemId)) {
              itemGroupId = group.groupId;
            }
          });
        }
        // Only include items that are not in groups being deleted
        return !itemGroupId || !groupIds.includes(itemGroupId);
      });

      const products = itemsForAnalytics.map((itemId) => {
        const product = productsDisplayed.find((item) => item.itemId === itemId);
        if (product) {
          return {
            itemId,
            quantity: getQuantity({ results: listProducts, id: itemId }),
            price: String(product?.pricing?.value)
          };
        }
        return null;
      }).filter((item) => item !== null);

      if (products.length > 0) {
        LIFE_CYCLE_EVENT_BUS.trigger(REMOVE_PRODUCT, { products });
      }

      const itemIdsMissingInPages = getItemIdsMissingInPages(itemsForAnalytics, listProducts, productsDisplayed, page);
      if (itemIdsMissingInPages?.length) {
        productInfoRequest({
          variables: {
            itemIds: itemIdsMissingInPages
          }
        });
      }
    }

    // Update selection state for removed items and groups (excluding items from deleted groups)
    const itemsToDeselect = individualItemIds.filter((itemId) => {
      // Find which group this item belongs to
      let itemGroupId = null;
      if (list?.groups) {
        list.groups.forEach((group) => {
          // Check both regular items and intent items
          if (group.items?.some((item) => item.itemId === itemId || item.intentId === itemId)) {
            itemGroupId = group.groupId;
          }
        });
      }
      // Only include items that are not in groups being deleted
      return !itemGroupId || !groupIds.includes(itemGroupId);
    });

    if (itemsToDeselect.length > 0) {
      onProductSelectionChange({ itemIds: itemsToDeselect, isAdd: false });
    }
    if (groupIds.length > 0) {
      onGroupSelectionChange({ groupIds, isAdd: false });
    }

    // Update displayed products - remove both individual items and items from deleted groups
    const allItemsToRemove = [...individualItemIds];

    // Add items from deleted groups
    groupIds.forEach((groupId) => {
      const group = list?.groups?.find((grp) => grp.groupId === groupId);
      if (group?.items) {
        group.items.forEach((item) => {
          allItemsToRemove.push(item.itemId);
        });
      }
    });

    const productDisplayedAfterDelete = productsDisplayed.filter((item) => !allItemsToRemove.includes(item.itemId));
    setProductsDisplayed([...productDisplayedAfterDelete]);
  };

  const onEditSuccess = ({ quantity, removed }) => {
    // Cancel replace mode when user performs another action (like quantity change)
    cancelReplaceMode();

    if (!removed) {
      refetch();
    }
  };

  const removeIntentItem = async ({ groupId, intentId }) => {
    // Cancel replace mode when user performs another action
    cancelReplaceMode();

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Find the target group and intent item
      const targetGroup = list.groups?.find((group) => group.groupId === actualGroupId);
      if (!targetGroup) return;

      const intentItem = targetGroup.items?.find((item) => item?.intentId === intentId);
      if (!intentItem) return;

      // Use RemoveItems operation to delete the specific intent
      await listOperationsMutation({
        variables: {
          operationName: 'RemoveItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intentId
            }]
          }]
        }
      });

      // Refetch to get updated data from backend
      await refetch();
      onEditSuccess({});
    } catch (deleteError) {
      // eslint-disable-next-line no-console
      console.error('Error removing intent item:', deleteError);
      // Refetch anyway to maintain consistency
      await refetch();
    }
  };

  const onSuccesfulMaterialsAdd = async () => {
    const response = await refetch();
    if (response?.data?.list?.id) {
      setIsAddedMaterials(true);
      productRefetch();
    }
  };

  const resolveItem = async ({
    groupId, intentId, itemId, quantity, itemOrder, intent
  }) => {
    const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

    try {
      // Simple mutation call - replace intent item with resolved product
      await listOperationsMutation({
        variables: {
          operationName: 'UpdateItems',
          id: list?.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              itemId,
              quantity,
              itemOrder,
              intent,
              intentId
            }]
          }]
        }
      });
      setResolvedIntentItemId((prev) => [...prev, itemId]);
      await refetch();
      // Fetch product data for the resolved item
      await loadItemsOnDemand([itemId]);
      onEditSuccess({});
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error resolving intent item:', err);
      await refetch();
    }
  };

  const duplicateIntent = async ({
    groupId,
    intent,
    quantity,
    itemOrder,
    sourceIntentId,
    sourceItemId
  }) => {
    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Find the target group
      const targetGroup = list.groups?.find((group) => group.groupId === actualGroupId);
      if (!targetGroup) return;

      // New item goes right after source item (itemOrder is already passed from the source)
      const newItemOrder = itemOrder + 1;

      // Build products array for UpdateItems:
      // Increment itemOrder for all items that have itemOrder > source itemOrder
      const itemsToReorder = targetGroup.items
        ?.filter((item) => item.itemOrder > itemOrder)
        ?.map((item) => ({
          itemId: item.itemId || undefined,
          intentId: item.intentId || undefined,
          itemOrder: item.itemOrder + 1
        })) || [];

      // First add the new item
      await listOperationsMutation({
        variables: {
          operationName: 'AddItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intent,
              quantity: quantity || 1,
              itemOrder: newItemOrder
            }]
          }]
        }
      });

      // Then update the order of items that come after to make room
      if (itemsToReorder.length > 0) {
        await listOperationsMutation({
          variables: {
            operationName: 'UpdateItems',
            id: list.id,
            groups: [{
              groupId: actualGroupId,
              products: itemsToReorder
            }]
          }
        });
      }

      // Trigger analytics event for duplicate
      LIFE_CYCLE_EVENT_BUS.trigger('intent.duplicate', {
        intent: {
          originalIntent: intent,
          sourceIntentId,
          sourceItemId
        },
        list: {
          id: list.id,
          name: list.name
        }
      });

      // Refetch to get updated list with new intent
      await refetch();
      onEditSuccess({});
    } catch (duplicateError) {
      // eslint-disable-next-line no-console
      console.error('Error duplicating intent:', duplicateError);
      await refetch();
    }
  };

  // executeReplace function - uses setReplaceMode and cancelReplaceMode defined earlier
  const executeReplace = useCallback(async ({ newItemId }) => {
    if (!replacingItemId || !newItemId) return;

    try {
      await replaceItemMutation({
        variables: {
          id: list?.id,
          oldItemId: replacingItemId,
          newItemId
        }
      });

      // Trigger analytics event for replace
      LIFE_CYCLE_EVENT_BUS.trigger('product.replace', {
        oldItemId: replacingItemId,
        newItemId,
        list: {
          id: list?.id,
          name: list?.name
        }
      });

      // Clear replace mode
      cancelReplaceMode();

      // Clear cache and refetch to get updated data
      clearListFromCache(list?.id);
      await refetch();
      onEditSuccess({});
    } catch (replaceError) {
      // eslint-disable-next-line no-console
      console.error('Error replacing item:', replaceError);
      cancelReplaceMode();
      await refetch();
    }
  }, [replacingItemId, list?.id, list?.name, replaceItemMutation, cancelReplaceMode, clearListFromCache, refetch]);

  if (showLoader) {
    return (
      <div data-component="ProductListLoader">
        <ListHeaderSkeleton isMobile={isMobile} />
        <ActionBarPlaceHolder />
        <ListViewPlaceHolder isMobile={isMobile} />
      </div>
    );
  }

  if (error) {
    const gqlErrors = returnArray(error?.graphQLErrors);
    const isPrivate = gqlErrors.some((err) => err?.message.includes(PRIVATE_LIST_ERROR));
    if (isPrivate) {
      return (
        <div className="sui-w-full sui-mt-10" data-component="ProductListError">
          <PrivateListMessaging />
        </div>
      );
    }

    const listDoesNotExist = gqlErrors.some((err) => err?.message.includes(NO_LIST_EXIST_ERROR));
    if (listDoesNotExist) {
      return (
        <div className="sui-w-full sui-mt-10" data-component="ProductListNotFound">
          <ListNotFoundMessaging isSharedList={isSharedList} />
        </div>
      );
    }
  }

  if (!list?.id) {
    return null;
  }

  const productDefaultVariables = {
    skipFavoriteCount: true,
    skipInstallServices: true,
    skipSpecificationGroup: false,
    skipKPF: true,
    skipSubscribeAndSave: true,
    storeId,
    zipCode,
    itemId: '', // Default itemId to satisfy dataModel requirements
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) return false;
    return mounted;
  };

  const context = {
    onDelete: onListDelete,
    list,
    resolveItem,
    removeIntentItem,
    duplicateIntent,
    productsDisplayed: productsDisplayed?.length ? productsDisplayed : productInfoProducts,
    onEditSuccess,
    removeListItems,
    selectedItems,
    selectedGroups,
    onProductSelectionChange,
    onGroupSelectionChange,
    isSharedList,
    onSaveSharedList,
    setProductsDisplayed,
    onSuccesfulMaterialsAdd,
    page,
    setPage,
    refetch,
    clearListFromCache,
    loadItemsOnDemand,
    // Product data management for ATC functionality
    productData,
    fetchProducts,
    loadedItemIds,
    // Loading states for progressive product fetching
    productInfoLoading,
    // Group expansion state management
    expandedGroups,
    setExpandedGroups,
    resolvedIntentItemId,
    // Replace feature state and functions
    replacingItemId,
    replacingIntentId,
    replacingGroupId,
    replacingIntent,
    replacingQuantity,
    isReplaceMode,
    setReplaceMode,
    cancelReplaceMode,
    executeReplace
  };

  const getQueryProviderKey = () => {
    const devicePart = isMobile ? 'mobile' : 'desktop';
    const sharePart = isSharedList ? 'Shared' : 'Private';
    return `list-product-${devicePart}-${sharePart}`;
  };

  const loadMoreProducts = () => {
    setPage(page + 1);
  };

  return (
    <div className="sui-w-full sui-bg-primary" data-component="ProductList">
      <QueryProvider cacheKey={getQueryProviderKey()}>
        <ProductListContext.Provider value={context}>
          <SaveSharedList showSaveSharedListBanner={showSaveSharedListBanner} />
          <ListHeader list={list} groupTotals={groupTotals} />
          <ProductListLoader loadMoreProducts={loadMoreProducts} page={page} storeId={storeId}>
            <ListView
              storeId={storeId}
              skipFn={skipFn}
              mountedFn={mountedFn}
              productDefaultVariables={productDefaultVariables}
              setIntersecting={setIntersecting}
              groupTotals={groupTotals}
              setGroupTotals={setGroupTotals}
            />
          </ProductListLoader>
        </ProductListContext.Provider>
      </QueryProvider>
    </div>
  );
};

ProductList.displayName = 'ProductList';

ProductList.propTypes = {
  id: string.isRequired,
  onDelete: func,
  isSharedList: bool,
  onSaveSharedList: func,
  onAnalyticsData: func
};

ProductList.defaultProps = {
  onDelete: null,
  isSharedList: false,
  onSaveSharedList: () => { },
  onAnalyticsData: undefined
};

ProductList.themeProps = {
  showListView: bool,
  showATQ: bool
};

ProductList.defaultThemeProps = {
  showListView: false,
  showATQ: false
};

const listResponse = {
  id: stringType(),
  itemCount: numberType(),
  itemIds: arrayType(stringType()),
  name: stringType(),
  listAccessType: stringType(),
  subTotal: numberType({ float: true }),
  url: stringType(),
  groups: arrayType(shapeType({
    groupId: stringType(),
    groupName: stringType(),
    groupOrder: numberType(),
    groupSubtotal: numberType({ float: true }),
    items: arrayType(shapeType({
      itemId: stringType(),
      quantity: numberType(),
      itemOrder: numberType(),
      comments: stringType()
    }))
  }))
};

ProductList.dataModel = extend({
  list: params({
    id: stringType().isRequired(),
    storeId: stringType(),
    sharedList: boolType()
  }).shape(listResponse),
  listProductDetails: params({
    id: stringType().isRequired(),
    storeId: stringType(),
    sharedList: boolType()
  }).shape({
    id: stringType(),
    itemCount: numberType(),
    itemIds: arrayType(stringType()),
    name: stringType(),
    listAccessType: stringType(),
    url: stringType(),
    groups: arrayType(shapeType({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: numberType(),
      items: arrayType(shapeType({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        intent: stringType(),
        intentId: stringType()
      }))
    }))
  }),
  products: params({
    itemIds: arrayType(stringType().isRequired()).isRequired(),
    dataSource: stringType(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayType(
        shapeType({
          tier: stringType(),
          program: stringType()
        })
      )
    })
  }).arrayOf(shapeType(ListView.dataModel.product)),
  listOperations: params({
    operationName: customType('Operation').enum(
      ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'Create'),
    id: stringType().isRequired(),
    groups: arrayType(customType('GroupInput').shape({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: numberType(),
      products: arrayType(customType('ProductInput').shape({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        intent: stringType(), // NEW: intent on creation
        intentId: stringType() // NEW: intentId for updates/removal
      }))
    })),
    name: stringType(),
    listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
  }).mutation().shape({
    id: stringType(),
    groups: arrayType(shapeType({
      groupId: stringType(),
      status: stringType(),
      items: arrayType(shapeType({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        status: stringType(),
        intent: stringType(), // NEW
        intentId: stringType() // NEW
      }))
    }))
  }),
  // Replace item mutation for swapping products in-place
  replaceItem: params({
    id: stringType().isRequired(),
    oldItemId: stringType(),
    newItemId: stringType()
  }).mutation().shape({
    id: stringType(),
    status: stringType()
  })
},
ListHeader,
ListView
);

export { ProductList };
